import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthWrapper';
import { Navigate, Link } from 'react-router-dom';
import FileUploader from '../../components/FileUploader';
import { getCollection, addDocument, updateDocument, deleteDocument, deleteFile } from '../../utils/dataService';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';

const AdminExhibits = () => {
  const { isAdmin } = useContext(AuthContext);
  const [exhibits, setExhibits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    exhibitNumber: '',
    title: '',
    description: '',
    category: '',
    date: '',
    filePath: '',
    fileURL: '',
    tags: ''
  });

  // Load exhibits data
  useEffect(() => {
    const loadExhibits = async () => {
      try {
        console.log('Attempting to load exhibits data...');
        
        // Try to fetch from the JSON file first
        try {
          const response = await fetch('/data/exhibits.json');
          if (response.ok) {
            const data = await response.json();
            console.log('Successfully loaded exhibits from JSON file:', data);
            setExhibits(data);
          } else {
            throw new Error(`Failed to fetch exhibits: ${response.status}`);
          }
        } catch (fetchError) {
          console.error('Error fetching exhibits JSON:', fetchError);
          
          // Fallback to getCollection utility if JSON fetch fails
          try {
            console.log('Attempting to fetch from database...');
            const data = await getCollection('exhibits');
            if (data && data.length > 0) {
              console.log('Successfully loaded exhibits from database:', data);
              setExhibits(data);
            } else {
              console.log('No exhibits found in database, using empty array');
              setExhibits([]);
            }
          } catch (dbError) {
            console.error('Error fetching from database:', dbError);
            console.log('Using empty exhibits array as fallback');
            setExhibits([]);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Unhandled error in loadExhibits:', error);
        setExhibits([]);
        setLoading(false);
      }
    };

    loadExhibits();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setFormData({
      id: item.id,
      exhibitNumber: item.exhibitNumber || '',
      title: item.title || '',
      description: item.description || '',
      category: item.category || '',
      date: item.date || '',
      filePath: item.filePath || '',
      fileURL: item.fileURL || '',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : ''
    });
    setShowForm(true);
  };

  const handleAddNew = () => {
    // Generate a unique ID
    const newId = `exhibit-${Date.now()}`;
    
    setEditItem(null);
    setFormData({
      id: newId,
      exhibitNumber: '',
      title: '',
      description: '',
      category: '',
      date: new Date().toISOString().split('T')[0],
      filePath: '',
      fileURL: '',
      tags: ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setEditItem(null);
    setShowForm(false);
    setFormData({
      id: '',
      exhibitNumber: '',
      title: '',
      description: '',
      category: '',
      date: '',
      filePath: '',
      fileURL: '',
      tags: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare data
    const exhibitData = {
      ...formData,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
    };
    
    if (editItem) {
      // Update existing exhibit
      try {
        // In a real implementation with Firestore:
        if (process.env.NODE_ENV === 'production') {
          await updateDocument('exhibits', exhibitData.id, exhibitData);
        }
        
        // Update local state
        const updatedExhibits = exhibits.map(item => 
          item.id === exhibitData.id ? exhibitData : item
        );
        setExhibits(updatedExhibits);
        alert('Exhibit updated successfully!');
      } catch (error) {
        console.error('Error updating exhibit:', error);
        alert('Failed to update exhibit. Please try again.');
      }
    } else {
      // Add new exhibit
      try {
        // In a real implementation with Firestore:
        if (process.env.NODE_ENV === 'production') {
          await addDocument('exhibits', exhibitData);
        }
        
        // Update local state
        setExhibits([...exhibits, exhibitData]);
        alert('New exhibit added successfully!');
      } catch (error) {
        console.error('Error adding exhibit:', error);
        alert('Failed to add exhibit. Please try again.');
      }
    }
    
    // Reset form
    handleCancel();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this exhibit? This will also delete any associated files.')) {
      try {
        // Get the exhibit to delete
        const exhibitToDelete = exhibits.find(item => item.id === id);
        
        if (exhibitToDelete && exhibitToDelete.filePath) {
          // Delete the associated file in production
          if (process.env.NODE_ENV === 'production') {
            await deleteFile(exhibitToDelete.filePath);
          }
        }
        
        // Delete the exhibit in production
        if (process.env.NODE_ENV === 'production') {
          await deleteDocument('exhibits', id);
        }
        
        // Update local state
        const updatedExhibits = exhibits.filter(item => item.id !== id);
        setExhibits(updatedExhibits);
        
        alert('Exhibit deleted successfully!');
      } catch (error) {
        console.error('Error deleting exhibit:', error);
        alert('Failed to delete exhibit. Please try again.');
      }
    }
  };

  const handleFileUploaded = (fileData) => {
    setFormData({
      ...formData,
      filePath: fileData.path,
      fileURL: fileData.url
    });
  };

  const handleAddExhibit = async (e) => {
    e.preventDefault();
    try {
      const newExhibit = {
        ...formData,
        date: formData.date || new Date().toISOString()
      };
      await addDoc(collection(db, 'exhibits'), newExhibit);
      alert('Exhibit added successfully!');
      setFormData({ title: '', description: '', category: '', date: '', filePath: '' });
    } catch (error) {
      console.error('Error adding exhibit:', error);
      alert('Failed to add exhibit. Please try again.');
    }
  };

  // Check if user is admin
  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-navy">Exhibits Management</h1>
        <div className="space-x-4">
          <button 
            onClick={handleAddNew}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
          >
            Add New Exhibit
          </button>
          <Link 
            to="/admin"
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-8">Loading exhibits data...</div>
      ) : (
        <div className="grid grid-cols-1 gap-8">
          {/* Edit/Add Form */}
          {showForm && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">
                {editItem ? 'Edit Exhibit' : 'Add New Exhibit'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Exhibit Number</label>
                    <input
                      type="text"
                      name="exhibitNumber"
                      value={formData.exhibitNumber}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    rows="3"
                  ></textarea>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">-- Select Category --</option>
                    <option value="document">Document</option>
                    <option value="photo">Photo</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. important, medical, evidence"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exhibit File</label>
                  {formData.filePath ? (
                    <div className="p-4 border rounded bg-gray-50">
                      <p className="mb-2">
                        <span className="font-semibold">Current file:</span> {formData.filePath.split('/').pop()}
                      </p>
                      {formData.fileURL && (
                        <a
                          href={formData.fileURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          View File
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          filePath: '',
                          fileURL: ''
                        })}
                        className="ml-4 text-red-600 hover:text-red-800"
                      >
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <FileUploader
                      folderPath="exhibits"
                      onFileUploaded={handleFileUploaded}
                      acceptedTypes="application/pdf,image/*,video/*,audio/*"
                      maxSize={20}
                    />
                  )}
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                  >
                    {editItem ? 'Update Exhibit' : 'Add Exhibit'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Exhibits List */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exhibit #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {exhibits.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{item.exhibitNumber}</td>
                    <td className="px-6 py-4">{item.title}</td>
                    <td className="px-6 py-4">{item.category || 'N/A'}</td>
                    <td className="px-6 py-4">{item.date || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditClick(item)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {exhibits.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No exhibits found. Add one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div>
        <h2>Loaded Exhibits in Admin:</h2>
        <pre>{JSON.stringify(exhibits, null, 2)}</pre>
      </div>
    </div>
  );
};

export default AdminExhibits;